// src/authpages/timetables/StudyTimetableGenerator .js
import React, { useState, useEffect } from 'react';
import { BookOpen, Clock, AlertTriangle, Info, Trash2, Sliders, Calendar, Plus, CheckCircle, XCircle } from 'lucide-react';

const StudyTimetableGenerator = ({ events = [], onGenerateSchedule }) => {
  // Subject state - initialized with an empty array instead of premade subjects
  const [subjects, setSubjects] = useState([]);
  
  const [newSubject, setNewSubject] = useState({ 
    name: '', 
    priority: 'medium', 
    weeklyHours: 5, 
    color: '#F4B400' 
  });
  
  // Study method state
  const [selectedMethod, setSelectedMethod] = useState('pomodoro');
  const [methodSettings, setMethodSettings] = useState({
    pomodoro: {
      studyDuration: 25,
      breakDuration: 5,
      sessionsBeforeLongBreak: 4,
      longBreakDuration: 15
    },
    flowMaster: {
      minSessionDuration: 60,
      idealSessionDuration: 90
    },
    energyCycle: {
      peakHours: [9, 10, 11, 16, 17, 18],
      lowEnergyHours: [13, 14],
      sessionDuringPeak: 50,
      sessionDuringNormal: 40,
      sessionDuringLow: 25
    },
    peakFocus: {
      morningPeakStart: 9,
      morningPeakEnd: 12,
      afternoonPeakStart: 16,
      afternoonPeakEnd: 19,
      sessionDuration: 45
    },
    custom: {
      sessionDuration: 60,
      breakDuration: 10
    }
  });
  
  // General settings
  const [settings, setSettings] = useState({
    earliestHour: 8,
    latestHour: 21,
    excludeExistingEvents: true,
    weekdays: [1, 2, 3, 4, 5], // Monday to Friday
    daysAhead: 7,
    minStudyBlock: 30, // Minimum time needed for a study block in minutes
    bufferTime: 15 // Buffer time between events in minutes
  });
  
  const [validationError, setValidationError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [freeTimeSlots, setFreeTimeSlots] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // No method settings dropdown
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  const availableColors = [
    '#4285F4', '#0F9D58', '#F4B400', '#DB4437', 
    '#673AB7', '#00ACC1', '#FF7043', '#3949AB'
  ];

  const studyMethods = [
    { 
      id: 'pomodoro', 
      name: 'Pomodoro Technique', 
      description: '25 minutes of study followed by 5 minutes break. Ideal for maintaining focus and preventing burnout.' 
    },
    { 
      id: 'flowMaster', 
      name: 'Flow Master Method', 
      description: '52 minutes of deep work followed by 17 minutes of rest. Optimized for achieving flow state.' 
    },
    { 
      id: 'energyCycle', 
      name: 'Energy Cycle Method', 
      description: '90 minutes of focused work aligned with your natural energy cycles, followed by 20 minutes of recovery.' 
    },
    { 
      id: 'peakFocus', 
      name: 'Peak Focus Method', 
      description: '58 minutes of intensive focus followed by 17 minutes of rest. Based on productivity research.' 
    },
    { 
      id: 'custom', 
      name: 'Custom Method', 
      description: 'Create your own custom study session pattern with personalized work and break durations.' 
    }
  ];

  // Analyze calendar for free time slots
  useEffect(() => {
    if (events.length > 0) {
      analyzeCalendar();
    }
  }, [events, settings.minStudyBlock, settings.bufferTime]);

  const addSubject = () => {
    if (!newSubject.name.trim()) {
      setValidationError('Subject name cannot be empty');
      return;
    }
    
    const subjectExists = subjects.some(
      subject => subject.name.toLowerCase() === newSubject.name.toLowerCase()
    );
    
    if (subjectExists) {
      setValidationError('A subject with this name already exists');
      return;
    }
    
    const newSubjectEntry = {
      ...newSubject,
      id: subjects.length > 0 ? Math.max(...subjects.map(s => s.id)) + 1 : 1,
      color: newSubject.color || getRandomColor()
    };

    setSubjects([...subjects, newSubjectEntry]);
    
    // Reset new subject form
    setNewSubject({ 
      name: '', 
      priority: 'medium', 
      weeklyHours: 5, 
      color: '#F4B400' 
    });
    setValidationError(null);
  };

  const removeSubject = (id) => {
    setSubjects(subjects.filter(subject => subject.id !== id));
  };

  const getRandomColor = () => {
    const usedColors = subjects.map(subject => subject.color);
    const availableUnusedColors = availableColors.filter(color => !usedColors.includes(color));
    
    return availableUnusedColors.length > 0 
      ? availableUnusedColors[Math.floor(Math.random() * availableUnusedColors.length)]
      : availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  const handleMethodSettingChange = (method, setting, value) => {
    setMethodSettings({
      ...methodSettings,
      [method]: {
        ...methodSettings[method],
        [setting]: value
      }
    });
  };

  const handleGeneralSettingChange = (setting, value) => {
    setSettings({
      ...settings,
      [setting]: value
    });
    
    // Re-analyze calendar when certain settings change
    if (['earliestHour', 'latestHour', 'weekdays', 'daysAhead'].includes(setting)) {
      analyzeCalendar();
    }
  };

  const handleWeekdayToggle = (day) => {
    const currentWeekdays = [...settings.weekdays];
    if (currentWeekdays.includes(day)) {
      handleGeneralSettingChange('weekdays', currentWeekdays.filter(d => d !== day));
    } else {
      handleGeneralSettingChange('weekdays', [...currentWeekdays, day].sort());
    }
  };

  // Find free time slots in the calendar
  const analyzeCalendar = () => {
    setIsAnalyzing(true);
    setFreeTimeSlots([]);
    
    try {
      // Get the date range to analyze
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(startDate.getDate() + settings.daysAhead);
      
      // Create a day-by-day analysis
      const freeSlots = [];
      
      for (let d = 0; d < settings.daysAhead; d++) {
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + d);
        
        // Skip days not in weekdays setting
        const dayOfWeek = currentDate.getDay();
        const adjustedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert Sunday (0) to 7 for easier filtering
        if (!settings.weekdays.includes(adjustedDayOfWeek)) {
          continue;
        }
        
        // Filter events for this day
        const dayEvents = events.filter(event => {
          const eventDate = new Date(event.start);
          return eventDate.getDate() === currentDate.getDate() && 
                 eventDate.getMonth() === currentDate.getMonth() && 
                 eventDate.getFullYear() === currentDate.getFullYear();
        });
        
        // Sort events by start time
        dayEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
        
        // Find free time between events
        const dayFreeSlots = findFreeSlotsInDay(currentDate, dayEvents);
        freeSlots.push(...dayFreeSlots);
      }
      
      setFreeTimeSlots(freeSlots);
      setIsAnalyzing(false);
    } catch (error) {
      console.error('Error analyzing calendar:', error);
      setIsAnalyzing(false);
    }
  };

  const findFreeSlotsInDay = (date, dayEvents) => {
    const freeSlots = [];
    
    // Set day start and end boundaries based on settings
    const dayStart = new Date(date);
    dayStart.setHours(settings.earliestHour, 0, 0, 0);
    
    const dayEnd = new Date(date);
    dayEnd.setHours(settings.latestHour, 0, 0, 0);
    
    // If no events, the entire day is free within boundaries
    if (dayEvents.length === 0) {
      freeSlots.push({
        start: dayStart,
        end: dayEnd,
        duration: (dayEnd - dayStart) / (1000 * 60) // Duration in minutes
      });
      return freeSlots;
    }
    
    // Check for free time before first event
    const firstEvent = dayEvents[0];
    const firstEventStart = new Date(firstEvent.start);
    
    if (firstEventStart > dayStart) {
      const duration = (firstEventStart - dayStart) / (1000 * 60);
      if (duration >= settings.minStudyBlock) {
        freeSlots.push({
          start: dayStart,
          end: new Date(firstEventStart.getTime() - settings.bufferTime * 60 * 1000),
          duration: duration - settings.bufferTime
        });
      }
    }
    
    // Check for free time between events
    for (let i = 0; i < dayEvents.length - 1; i++) {
      const currentEventEnd = new Date(dayEvents[i].end);
      const nextEventStart = new Date(dayEvents[i + 1].start);
      
      const duration = (nextEventStart - currentEventEnd) / (1000 * 60);
      if (duration >= settings.minStudyBlock + settings.bufferTime * 2) {
        freeSlots.push({
          start: new Date(currentEventEnd.getTime() + settings.bufferTime * 60 * 1000),
          end: new Date(nextEventStart.getTime() - settings.bufferTime * 60 * 1000),
          duration: duration - settings.bufferTime * 2
        });
      }
    }
    
    // Check for free time after last event
    const lastEvent = dayEvents[dayEvents.length - 1];
    const lastEventEnd = new Date(lastEvent.end);
    
    if (lastEventEnd < dayEnd) {
      const duration = (dayEnd - lastEventEnd) / (1000 * 60);
      if (duration >= settings.minStudyBlock + settings.bufferTime) {
        freeSlots.push({
          start: new Date(lastEventEnd.getTime() + settings.bufferTime * 60 * 1000),
          end: dayEnd,
          duration: duration - settings.bufferTime
        });
      }
    }
    
    return freeSlots;
  };

  // Formats date to display in UI
  const formatTimeSlot = (slot) => {
    const startTime = slot.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = slot.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const date = slot.start.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const duration = Math.round(slot.duration);
    
    return `${date}, ${startTime} - ${endTime} (${duration} mins)`;
  };

  // Generate study schedule based on free time and selected method
  const generateStudySchedule = () => {
    if (subjects.length === 0) {
      setValidationError('Add at least one subject to generate a schedule');
      return;
    }
    
    if (freeTimeSlots.length === 0) {
      setValidationError('No suitable free time slots found in your calendar');
      return;
    }
    
    try {
      // Calculate total minutes needed per subject based on weeklyHours
      const subjectMinutes = subjects.map(subject => ({
        ...subject,
        minutesNeeded: subject.weeklyHours * 60,
        minutesAssigned: 0
      }));

      // Sort subjects by priority
      const sortedSubjects = [...subjectMinutes].sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      // Generate study sessions based on method
      const studySessions = [];
      
      // Deep copy of free slots to work with
      const availableSlots = [...freeTimeSlots].sort((a, b) => a.start - b.start);
      
      // Create specific algorithm based on study method
      const generateSessions = () => {
        switch (selectedMethod) {
          case 'pomodoro':
            generatePomodoroSessions(sortedSubjects, availableSlots, studySessions);
            break;
          case 'flowMaster':
            generateFlowMasterSessions(sortedSubjects, availableSlots, studySessions);
            break;
          case 'energyCycle':
            generateEnergyCycleSessions(sortedSubjects, availableSlots, studySessions);
            break;
          case 'peakFocus':
            generatePeakFocusSessions(sortedSubjects, availableSlots, studySessions);
            break;
          case 'custom':
            generateCustomSessions(sortedSubjects, availableSlots, studySessions);
            break;
          default:
            generatePomodoroSessions(sortedSubjects, availableSlots, studySessions);
        }
      };

      generateSessions();

      // Pass generated sessions to parent component
      if (onGenerateSchedule) {
        onGenerateSchedule(studySessions);
      }
      
      setGenerationStatus(`Successfully generated ${studySessions.length} study sessions for ${subjects.length} subjects`);
    } catch (error) {
      console.error('Error generating study schedule:', error);
      setValidationError('Error generating study schedule. Please try again.');
    }
  };
  
  // Pomodoro technique session generation
  const generatePomodoroSessions = (subjects, availableSlots, studySessions) => {
    const { studyDuration, breakDuration, sessionsBeforeLongBreak, longBreakDuration } = methodSettings.pomodoro;
    
    // Calculate how many Pomodoro cycles can fit into each time slot
    for (const slot of availableSlots) {
      // Skip slots that are too short for at least one Pomodoro
      if (slot.duration < studyDuration) continue;
      
      let remainingMinutes = slot.duration;
      let currentTime = new Date(slot.start);
      let pomodoroCount = 0;
      
      // Keep adding Pomodoros until slot is filled
      while (remainingMinutes >= studyDuration) {
        // Determine which subject to add next
        const nextSubject = findNextSubject(subjects);
        if (!nextSubject) break; // No more subjects need time
        
        // Add study session
        const sessionEnd = new Date(currentTime.getTime() + studyDuration * 60 * 1000);
        
        studySessions.push({
          id: `study-${studySessions.length}`,
          title: `${nextSubject.name} (Pomodoro)`,
          start: new Date(currentTime),
          end: sessionEnd,
          color: nextSubject.color,
          provider: 'Study Schedule'
        });
        
        // Update subject's assigned time
        nextSubject.minutesAssigned += studyDuration;
        
        // Update time tracker for next session
        currentTime = sessionEnd;
        remainingMinutes -= studyDuration;
        pomodoroCount++;
        
        // Add break if there's room
        if (remainingMinutes >= breakDuration && remainingMinutes >= studyDuration) {
          // Add a regular break or a long break
          const isLongBreak = pomodoroCount % sessionsBeforeLongBreak === 0;
          const breakTime = isLongBreak ? longBreakDuration : breakDuration;
          
          if (remainingMinutes >= breakTime) {
            currentTime = new Date(currentTime.getTime() + breakTime * 60 * 1000);
            remainingMinutes -= breakTime;
          }
        }
      }
    }
  };

  // Flow Master session generation
  const generateFlowMasterSessions = (subjects, availableSlots, studySessions) => {
    const { minSessionDuration, idealSessionDuration } = methodSettings.flowMaster;
    
    // Sort slots by duration (longest first to prioritize flow sessions)
    const sortedSlots = [...availableSlots].sort((a, b) => b.duration - a.duration);
    
    for (const slot of sortedSlots) {
      // Skip slots that are too short
      if (slot.duration < minSessionDuration) continue;
      
      // Determine which subject to add
      const nextSubject = findNextSubject(subjects);
      if (!nextSubject) break; // No more subjects need time
      
      // Calculate session duration based on slot size (52 minutes for Flow Master)
      let sessionDuration = Math.min(slot.duration, 52);
      
      // Create the study session
      const sessionEnd = new Date(slot.start.getTime() + sessionDuration * 60 * 1000);
      
      studySessions.push({
        id: `study-${studySessions.length}`,
        title: `${nextSubject.name} (Flow)`,
        start: new Date(slot.start),
        end: sessionEnd,
        color: nextSubject.color,
        provider: 'Study Schedule'
      });
      
      // Update subject's assigned time
      nextSubject.minutesAssigned += sessionDuration;
      
      // If there's enough time left for a rest period (17 minutes)
      if (slot.duration - sessionDuration >= 17) {
        const restEnd = new Date(sessionEnd.getTime() + 17 * 60 * 1000);
        
        studySessions.push({
          id: `rest-${studySessions.length}`,
          title: `Rest Period`,
          start: sessionEnd,
          end: restEnd,
          color: '#CCCCCC',
          provider: 'Study Schedule'
        });
      }
    }
  };

  // Energy Cycle session generation
  const generateEnergyCycleSessions = (subjects, availableSlots, studySessions) => {
    // Sort slots by time of day
    const sortedSlots = [...availableSlots].sort((a, b) => a.start - b.start);
    
    for (const slot of sortedSlots) {
      // Skip slots that are too short for a 90-minute session
      if (slot.duration < 90) continue;
      
      // Determine which subject to add
      const nextSubject = findNextSubject(subjects);
      if (!nextSubject) break; // No more subjects need time
      
      // 90 minutes of focused work
      let sessionDuration = Math.min(slot.duration, 90);
      
      // Create the study session
      const sessionEnd = new Date(slot.start.getTime() + sessionDuration * 60 * 1000);
      
      studySessions.push({
        id: `study-${studySessions.length}`,
        title: `${nextSubject.name} (Energy Cycle)`,
        start: new Date(slot.start),
        end: sessionEnd,
        color: nextSubject.color,
        provider: 'Study Schedule'
      });
      
      // Update subject's assigned time
      nextSubject.minutesAssigned += sessionDuration;
      
      // If there's enough time left for a recovery period (20 minutes)
      if (slot.duration - sessionDuration >= 20) {
        const recoveryEnd = new Date(sessionEnd.getTime() + 20 * 60 * 1000);
        
        studySessions.push({
          id: `recovery-${studySessions.length}`,
          title: `Recovery Period`,
          start: sessionEnd,
          end: recoveryEnd,
          color: '#CCCCCC',
          provider: 'Study Schedule'
        });
      }
    }
  };

  // Peak Focus session generation
  const generatePeakFocusSessions = (subjects, availableSlots, studySessions) => {
    // Sort slots by time of day
    const sortedSlots = [...availableSlots].sort((a, b) => a.start - b.start);
    
    for (const slot of sortedSlots) {
      // Skip slots that are too short for a 58-minute session
      if (slot.duration < 58) continue;
      
      // Determine which subject to add
      const nextSubject = findNextSubject(subjects);
      if (!nextSubject) break; // No more subjects need time
      
      // 58 minutes of focused work
      let sessionDuration = Math.min(slot.duration, 58);
      
      // Create the study session
      const sessionEnd = new Date(slot.start.getTime() + sessionDuration * 60 * 1000);
      
      studySessions.push({
        id: `study-${studySessions.length}`,
        title: `${nextSubject.name} (Peak Focus)`,
        start: new Date(slot.start),
        end: sessionEnd,
        color: nextSubject.color,
        provider: 'Study Schedule'
      });
      
      // Update subject's assigned time
      nextSubject.minutesAssigned += sessionDuration;
      
      // If there's enough time left for a rest period (17 minutes)
      if (slot.duration - sessionDuration >= 17) {
        const restEnd = new Date(sessionEnd.getTime() + 17 * 60 * 1000);
        
        studySessions.push({
          id: `rest-${studySessions.length}`,
          title: `Rest Period`,
          start: sessionEnd,
          end: restEnd,
          color: '#CCCCCC',
          provider: 'Study Schedule'
        });
      }
    }
  };

  // Custom session generation
  const generateCustomSessions = (subjects, availableSlots, studySessions) => {
    const { sessionDuration, breakDuration } = methodSettings.custom;
    
    for (const slot of availableSlots) {
      // Skip slots that are too short
      if (slot.duration < sessionDuration) continue;
      
      let remainingMinutes = slot.duration;
      let currentTime = new Date(slot.start);
      
      while (remainingMinutes >= sessionDuration) {
        const nextSubject = findNextSubject(subjects);
        if (!nextSubject) break; // No more subjects need time
        
        // Create study session
        const sessionEnd = new Date(currentTime.getTime() + sessionDuration * 60 * 1000);
        
        studySessions.push({
          id: `study-${studySessions.length}`,
          title: `${nextSubject.name} (Custom)`,
          start: new Date(currentTime),
          end: sessionEnd,
          color: nextSubject.color,
          provider: 'Study Schedule'
        });
        
        // Update subject's assigned time
        nextSubject.minutesAssigned += sessionDuration;
        
        // Update time tracker
        currentTime = sessionEnd;
        remainingMinutes -= sessionDuration;
        
        // Add break if there's room
        if (remainingMinutes >= breakDuration && remainingMinutes >= sessionDuration) {
          const breakEnd = new Date(currentTime.getTime() + breakDuration * 60 * 1000);
          
          studySessions.push({
            id: `break-${studySessions.length}`,
            title: `Break`,
            start: currentTime,
            end: breakEnd,
            color: '#CCCCCC',
            provider: 'Study Schedule'
          });
          
          currentTime = breakEnd;
          remainingMinutes -= breakDuration;
        }
      }
    }
  };
  
  // Helper function to find the next subject to assign time to
  const findNextSubject = (subjectList) => {
    // First try subjects that still need time, sorted by completion percentage
    const incompleteSubjects = subjectList.filter(s => s.minutesAssigned < s.minutesNeeded);
    
    if (incompleteSubjects.length === 0) return null;
    
    // Sort by completion percentage (least complete first)
    incompleteSubjects.sort((a, b) => {
      const aCompletion = a.minutesAssigned / a.minutesNeeded;
      const bCompletion = b.minutesAssigned / b.minutesNeeded;
      return aCompletion - bCompletion;
    });
    
    return incompleteSubjects[0];
  };

  // Calculate subject progress
  const getSubjectProgress = (subject) => {
    const total = subject.weeklyHours * 60; // in minutes
    const assigned = subject.minutesAssigned || 0;
    return Math.min(100, Math.round((assigned / total) * 100));
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {validationError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
          <div className="flex items-center">
            <AlertTriangle className="mr-2" size={20} />
            <span>{validationError}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="bg-gray-100 px-4 py-3 rounded-t-lg border-b">
          <h2 className="text-lg font-semibold flex items-center">
            <BookOpen className="text-blue-600 mr-2" size={20} /> 
            Study Subjects
          </h2>
        </div>
        <div className="p-4">
          {/* Subject List */}
          {subjects.length === 0 ? (
            <div className="bg-blue-50 text-blue-700 p-4 rounded mb-4">
              No subjects added yet. Add a subject to begin.
            </div>
          ) : (
            <div className="mb-4 space-y-3">
              {subjects.map(subject => (
                <div key={subject.id} className="border rounded p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      <div 
                        className="mr-2 rounded-full" 
                        style={{ 
                          width: '16px', 
                          height: '16px', 
                          backgroundColor: subject.color 
                        }}
                      />
                      <h3 className="font-bold text-gray-800">{subject.name}</h3>
                      <span 
                        className={`ml-2 text-xs px-2 py-1 rounded-full text-white ${
                          subject.priority === 'high' ? 'bg-red-500' : 
                          subject.priority === 'medium' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`}
                      >
                        {subject.priority}
                      </span>
                    </div>
                    <button 
                      className="text-red-500 hover:text-red-700 p-1 rounded"
                      onClick={() => removeSubject(subject.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3/5 mr-3">
                      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            subject.priority === 'high' ? 'bg-red-500' : 
                            subject.priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${getSubjectProgress(subject)}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {subject.weeklyHours} hours/week ({subject.weeklyHours * 60} minutes)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Subject Form */}
          <div className="bg-gray-50 rounded p-4 border">
            <h3 className="font-medium mb-3">Add New Subject</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Mathematics, Physics"
                  value={newSubject.name}
                  onChange={(e) => setNewSubject({
                    ...newSubject, 
                    name: e.target.value
                  })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newSubject.priority}
                    onChange={(e) => setNewSubject({
                      ...newSubject, 
                      priority: e.target.value
                    })}
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weekly Hours
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    className="w-full px-3 py-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newSubject.weeklyHours}
                    onChange={(e) => setNewSubject({
                      ...newSubject, 
                      weeklyHours: parseInt(e.target.value)
                    })}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex flex-wrap">
                  {availableColors.map(color => (
                    <div
                      key={color}
                      onClick={() => setNewSubject({ ...newSubject, color })}
                      className={`rounded-full mr-2 mb-2 border-2 cursor-pointer ${newSubject.color === color ? 'border-blue-500 shadow-lg' : 'border-transparent'}`}
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: color
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-end">
                <button 
                  onClick={addSubject} 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded flex items-center justify-center"
                >
                  <Plus size={18} className="mr-1" /> Add Subject
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="bg-gray-100 px-4 py-3 rounded-t-lg border-b">
          <h2 className="text-lg font-semibold flex items-center">
            <Clock className="text-blue-600 mr-2" size={20} /> 
            Study Method
          </h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {studyMethods.map(method => (
              <div 
                key={method.id}
                className={`border rounded p-4 cursor-pointer h-full transition ${selectedMethod === method.id ? 'border-blue-500 shadow-md bg-blue-50' : 'hover:shadow-sm hover:bg-gray-50'}`}
                onClick={() => setSelectedMethod(method.id)}
              >
                <div className="flex items-center mb-2">
                  <div 
                    className={`mr-2 rounded-full flex items-center justify-center w-6 h-6 ${selectedMethod === method.id ? 'bg-blue-600' : 'bg-gray-200'}`}
                  >
                    {selectedMethod === method.id && (
                      <CheckCircle size={16} className="text-white" />
                    )}
                  </div>
                  <h3 className="font-medium">{method.name}</h3>
                </div>
                
                {method.id === 'pomodoro' && (
                  <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-medium mb-2 inline-block">
                    25min study + 5min break
                  </div>
                )}
                
                {method.id === 'flowMaster' && (
                  <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-xs font-medium mb-2 inline-block">
                    52min work + 17min rest
                  </div>
                )}
                
                {method.id === 'energyCycle' && (
                  <div className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-medium mb-2 inline-block">
                    90min focus + 20min recovery
                  </div>
                )}
                
                {method.id === 'peakFocus' && (
                  <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-xs font-medium mb-2 inline-block">
                    58min focus + 17min rest
                  </div>
                )}
                
                {method.id === 'custom' && (
                  <div className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-xs font-medium mb-2 inline-block">
                    Customize work + break times
                  </div>
                )}
                
                <p className="text-sm text-gray-600">{method.description}</p>
              </div>
            ))}
          </div>
          

          
          <div className="border rounded overflow-hidden mb-4">
            <div 
              className="flex items-center px-4 py-3 bg-gray-50 border-b cursor-pointer"
              onClick={() => setShowCalendarSettings(!showCalendarSettings)}
            >
              <Calendar size={18} className="mr-2 text-blue-600" /> 
              <span className="font-medium">Calendar Settings</span>
              <span className="ml-auto">
                {showCalendarSettings ? '−' : '+'}
              </span>
            </div>
            
            {showCalendarSettings && (
              <div className="p-4">
                {/* Calendar settings content would go here */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Study Days
                    </label>
                    <div className="flex">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                        <div 
                          key={day}
                          onClick={() => handleWeekdayToggle(index + 1)} // 1 for Monday, 7 for Sunday
                          className={`
                            flex items-center justify-center 
                            rounded-full mr-2 cursor-pointer
                            w-9 h-9
                            ${settings.weekdays.includes(index + 1) ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}
                          `}
                        >
                          {day.charAt(0)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="border rounded overflow-hidden">
            <div 
              className="flex items-center px-4 py-3 bg-gray-50 border-b cursor-pointer"
              onClick={() => setShowTimeSlots(!showTimeSlots)}
            >
              <Calendar size={18} className="mr-2 text-blue-600" /> 
              <span className="font-medium">
                Available Time Slots ({freeTimeSlots.length})
              </span>
              <span className="ml-auto">
                {showTimeSlots ? '−' : '+'}
              </span>
            </div>
            
            {showTimeSlots && (
              <div className="p-4">
                {isAnalyzing ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-blue-600" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Analyzing your calendar...</p>
                  </div>
                ) : freeTimeSlots.length === 0 ? (
                  <div className="bg-yellow-50 text-yellow-700 p-4 rounded">
                    No suitable free time slots found in your calendar. Try adjusting your settings.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <ul className="divide-y">
                      {freeTimeSlots.map((slot, index) => (
                        <li key={index} className="py-2 flex justify-between items-center">
                          <div>
                            <small className="text-gray-500">
                              {new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(slot.start)}
                            </small>
                            <div>{formatTimeSlot(slot)}</div>
                          </div>
                          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                            {Math.round(slot.duration)} mins
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-3">
                  <button
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 flex items-center"
                    onClick={analyzeCalendar}
                    disabled={isAnalyzing}
                  >
                    <RefreshIcon size={16} className="mr-1" />
                    Refresh Analysis
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <button 
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg rounded-lg flex items-center justify-center mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={generateStudySchedule}
        disabled={subjects.length === 0 || freeTimeSlots.length === 0}
      >
        <Calendar className="mr-2" size={20} />
        Generate Study Schedule
      </button>

      {/* Generation Status */}
      {generationStatus && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded">
          <div className="flex items-center">
            <CheckCircle className="mr-2 text-green-500" size={20} />
            <div>{generationStatus}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Define the RefreshIcon component since it's used in the code
const RefreshIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={props.size || 24}
    height={props.size || 24}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={props.className || ''}
  >
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

export default StudyTimetableGenerator;